import * as XLSX from 'xlsx';

export const downloadAnalysisAsXLSX = (data: any[], customFilename: string = '') => {
  // Prepare filename
  const today = new Date();
  const dateStr = today.toLocaleDateString('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).replace(/\//g, '-');

  const filename = customFilename
    ? `${customFilename.replace(/[^a-zA-Z0-9]/g, '_')}-${dateStr}.xlsx`
    : `NexusAnalysis-${dateStr}.xlsx`;

  // Column order for output
  const columnOrder = [
    "State",
    "Year",
    "Nexus Rule",
    "Met Nexus",
    "Crossed on",
    "Crossed type",
    "Threshold Amt",
    "Customer Data Total",
    "Threshold - Total Pct",
    "Threshold Count",
    "Customer Data Count",
    "Threshold - Count Pct",
  ];

  // Filter data to only include each state once if it crossed nexus in the first year
  const stateMap: Record<string, { crossedYear: number | null }> = {};
  const filteredData = data.filter(row => {
    const state = row.State;
    const year = parseInt(row.Year);
    
    // If we haven't seen this state before, add it to our map
    if (!stateMap[state]) {
      stateMap[state] = {
        crossedYear: row["Met Nexus"] === "Yes" ? year : null
      };
      return true;
    } 
    
    // If this state already crossed nexus in a previous year, skip this row
    const crossedYear = stateMap[state]?.crossedYear;
    if (crossedYear !== null && crossedYear !== undefined && crossedYear < year) {
      return false;
    }
    
    return true;
  });

  // Format cells with styles
  const formattedData = filteredData.map(row => {
    let crossedType = '';
    
    if (row["Met Nexus"] === "Yes") {
      if (
        row["Threshold - Total Pct"] >= 100 &&
        row["Threshold - Count Pct"] >= 100
      ) {
        crossedType = "Order Amount & Count";
      } else if (row["Threshold - Total Pct"] >= 100) {
        crossedType = "Order Amount";
      } else if (row["Threshold - Count Pct"] >= 100) {
        crossedType = "Order Count";
      }
    }
    
    const baseStyle = { alignment: { horizontal: "left" } };
    const metNexusStyle =
      row["Met Nexus"] === "Yes"
        ? {
            fill: { fgColor: { rgb: "E8EAF6" }, patternType: "solid" },
          }
        : {};
    
    // Enhanced column formatting based on state
    return {
      State: {
        v: row.State,
        s: { ...baseStyle, font: { bold: true }, ...metNexusStyle },
      },
      Year: {
        v: row.Year || "",
        s: { ...baseStyle, ...metNexusStyle },
      },
      "Nexus Rule": {
        v: row["Nexus Rule"] || "",
        s: { ...baseStyle, ...metNexusStyle },
      },
      "Met Nexus": {
        v: row["Met Nexus"] || "",
        s: {
          ...baseStyle,
          ...metNexusStyle,
          font: {
            color: { rgb: row["Met Nexus"] === "Yes" ? "2E7D32" : "000000" },
            bold: row["Met Nexus"] === "Yes",
          },
        },
      },
      "Crossed on": {
        v: row["Crossed on"] || "",
        s: { ...baseStyle, ...metNexusStyle },
      },
      "Crossed type": {
        v: crossedType,
        s: { ...baseStyle, ...metNexusStyle },
      },
      "Threshold Amt": {
        v: row["Threshold Amt"] || 0,
        s: {
          ...baseStyle,
          ...metNexusStyle,
          numFmt: "$#,##0.00",
        },
      },
      "Customer Data Total": {
        v: row["Customer Data Total"] || 0,
        s: {
          ...baseStyle,
          ...metNexusStyle,
          numFmt: "$#,##0.00",
        },
      },
      "Threshold - Total Pct": {
        v: row["Threshold - Total Pct"] || 0,
        s: {
          ...baseStyle,
          ...metNexusStyle,
          numFmt: "0.00%",
          font: {
            color: {
              rgb: row["Threshold - Total Pct"] >= 100 ? "C62828" : "000000",
            },
            bold: row["Threshold - Total Pct"] >= 100,
          },
        },
      },
      "Threshold Count": {
        v: row["Threshold Count"] || 0,
        s: { ...baseStyle, ...metNexusStyle, numFmt: "#,##0" },
      },
      "Customer Data Count": {
        v: row["Customer Data Count"] || 0,
        s: { ...baseStyle, ...metNexusStyle, numFmt: "#,##0" },
      },
      "Threshold - Count Pct": {
        v: row["Threshold - Count Pct"] || 0,
        s: {
          ...baseStyle,
          ...metNexusStyle,
          numFmt: "0.00%",
          font: {
            color: {
              rgb: row["Threshold - Count Pct"] >= 100 ? "C62828" : "000000",
            },
            bold: row["Threshold - Count Pct"] >= 100,
          },
        },
      },
    };
  });

  // Create worksheet
  const worksheet = XLSX.utils.json_to_sheet([]);
  
  // Set column widths
  const colWidths = [
    { wch: 15 }, // State
    { wch: 10 }, // Year
    { wch: 30 }, // Nexus Rule
    { wch: 10 }, // Met Nexus
    { wch: 15 }, // Crossed on
    { wch: 20 }, // Crossed type
    { wch: 14 }, // Threshold Amt
    { wch: 18 }, // Customer Data Total
    { wch: 18 }, // Threshold - Total Pct
    { wch: 14 }, // Threshold Count
    { wch: 18 }, // Customer Data Count
    { wch: 18 }, // Threshold - Count Pct
  ];
  
  worksheet['!cols'] = colWidths;
  
  // Add rows
  XLSX.utils.sheet_add_json(worksheet, formattedData.map(row => {
    const plainRow: any = {};
    columnOrder.forEach(col => {
      plainRow[col] = row[col as keyof typeof row]?.v;
    });
    return plainRow;
  }), { origin: 'A1', header: columnOrder });
  
  // Apply cell styles
  formattedData.forEach((row, rowIndex) => {
    columnOrder.forEach((col, colIndex) => {
      const cellAddress = XLSX.utils.encode_cell({ r: rowIndex + 1, c: colIndex });
      if (!worksheet[cellAddress]) worksheet[cellAddress] = {};
      worksheet[cellAddress].s = row[col as keyof typeof row]?.s;
    });
  });
  
  // Create workbook
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Nexus Analysis');
  
  // Generate and download file
  XLSX.writeFile(workbook, filename);
};